import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import CssBaseline from '@mui/material/CssBaseline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ReplyIcon from '@mui/icons-material/Reply';
import ShareIcon from '@mui/icons-material/Share';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ChatIcon from '@mui/icons-material/Chat';
import WarningIcon from '@mui/icons-material/Warning';
import ScienceIcon from '@mui/icons-material/Science';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import WaterIcon from '@mui/icons-material/Water';
import SensorsIcon from '@mui/icons-material/Sensors';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import PhotoIcon from '@mui/icons-material/Photo';
import AppNavbar from '../dashboard/components/AppNavbar';
import SideMenu from '../dashboard/components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import { DataModeProvider, DATA_MODES } from '../context/DataModeContext';
import { GrowProvider } from '../dashboard/context/GrowContext';
import { CommunityProvider, useCommunity } from '../context/CommunityContext';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../dashboard/theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

// Icon mapping for topics
const topicIcons = {
  chat: ChatIcon,
  warning: WarningIcon,
  science: ScienceIcon,
  lightbulb: LightbulbIcon,
  water: WaterIcon,
  sensors: SensorsIcon,
  agriculture: AgricultureIcon,
  photo: PhotoIcon,
};

// Format date
function formatDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Format relative time
function formatRelativeTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// Simple markdown-like rendering
function renderContent(content) {
  // Split by code blocks first
  const parts = content.split(/(```[\s\S]*?```)/g);

  return parts.map((part, index) => {
    if (part.startsWith('```')) {
      // Code block
      const code = part.slice(3, -3).trim();
      return (
        <Box
          key={index}
          component="pre"
          sx={{
            bgcolor: 'action.hover',
            p: 2,
            borderRadius: 1,
            overflow: 'auto',
            fontSize: '0.875rem',
            fontFamily: 'monospace',
            my: 1,
          }}
        >
          <code>{code}</code>
        </Box>
      );
    }

    // Process regular text
    const lines = part.split('\n');
    return lines.map((line, lineIndex) => {
      // Headers
      if (line.startsWith('## ')) {
        return (
          <Typography key={`${index}-${lineIndex}`} variant="h6" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            {line.slice(3)}
          </Typography>
        );
      }
      if (line.startsWith('# ')) {
        return (
          <Typography key={`${index}-${lineIndex}`} variant="h5" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
            {line.slice(2)}
          </Typography>
        );
      }

      // Lists
      if (line.startsWith('- ')) {
        return (
          <Typography key={`${index}-${lineIndex}`} variant="body1" component="li" sx={{ ml: 2 }}>
            {line.slice(2)}
          </Typography>
        );
      }
      if (/^\d+\.\s/.test(line)) {
        return (
          <Typography key={`${index}-${lineIndex}`} variant="body1" component="li" sx={{ ml: 2, listStyleType: 'decimal' }}>
            {line.replace(/^\d+\.\s/, '')}
          </Typography>
        );
      }

      // Empty line = paragraph break
      if (line.trim() === '') {
        return <Box key={`${index}-${lineIndex}`} sx={{ height: 8 }} />;
      }

      // Bold text — split by **bold** markers and render as React elements (safe, no innerHTML)
      const boldParts = line.split(/\*\*(.*?)\*\*/g);
      return (
        <Typography
          key={`${index}-${lineIndex}`}
          variant="body1"
          sx={{ lineHeight: 1.7 }}
        >
          {boldParts.map((segment, i) =>
            i % 2 === 1
              ? <strong key={i}>{segment}</strong>
              : segment
          )}
        </Typography>
      );
    });
  });
}

function ReplyCard({ reply, isAccepted }) {
  const { getUser } = useCommunity();
  const user = getUser(reply.authorId);

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        borderColor: isAccepted ? 'success.main' : 'divider',
        bgcolor: isAccepted ? 'success.50' : 'background.paper',
      }}
    >
      <Stack spacing={2}>
        {/* Reply Header */}
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
            {reply.author[0].toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {reply.author}
              </Typography>
              {user && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  ({user.reputation} rep)
                </Typography>
              )}
              {isAccepted && (
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                  label="Accepted Answer"
                  size="small"
                  color="success"
                  sx={{ height: 20 }}
                />
              )}
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {formatRelativeTime(reply.createdAt)}
            </Typography>
          </Box>
        </Stack>

        {/* Reply Content */}
        <Box sx={{ pl: 7 }}>
          {renderContent(reply.content)}
        </Box>

        {/* Reply Actions */}
        <Stack direction="row" spacing={1} sx={{ pl: 7 }}>
          <Button
            size="small"
            startIcon={<ThumbUpOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {reply.likes}
          </Button>
          <Button
            size="small"
            startIcon={<ReplyIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Reply
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
}

function PostContent() {
  const { postId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    getPost,
    getReplies,
    getUser,
    forumTopics,
    canPost,
    isDemo,
    createReply,
  } = useCommunity();

  const [post, setPost] = React.useState(null);
  const [replies, setReplies] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [replyContent, setReplyContent] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const basePath = location.pathname.startsWith('/demo') ? '/demo' : '/app';

  // Load post and replies
  React.useEffect(() => {
    const loadPost = async () => {
      setLoading(true);
      const foundPost = getPost(postId);
      setPost(foundPost);

      if (foundPost) {
        const postReplies = await getReplies(postId);
        setReplies(postReplies);
      }
      setLoading(false);
    };

    loadPost();
  }, [postId, getPost, getReplies]);

  const topic = post ? forumTopics.find(t => t.id === post.topicId) : null;
  const TopicIcon = topic ? (topicIcons[topic.icon] || ChatIcon) : ChatIcon;
  const author = post ? getUser(post.authorId) : null;

  // Handle reply submission
  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim() || !canPost) return;

    setSubmitting(true);
    try {
      const newReply = await createReply(postId, replyContent);
      setReplies(prev => [...prev, newReply]);
      setReplyContent('');
    } catch (err) {
      console.error('Error creating reply:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ color: 'text.secondary' }}>Loading post...</Typography>
      </Box>
    );
  }

  if (!post) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Post not found</Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`${basePath}/forum`)}
        >
          Back to Forum
        </Button>
      </Box>
    );
  }

  const acceptedAnswer = replies.find(r => r.isAcceptedAnswer);
  const otherReplies = replies.filter(r => !r.isAcceptedAnswer);

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '900px' }, mx: 'auto', p: 3 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`${basePath}/forum`)}
        sx={{ mb: 2, textTransform: 'none' }}
      >
        Back to Forum
      </Button>

      {/* Main Post Card */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <Box sx={{ p: 3 }}>
          {/* Post Header */}
          <Stack spacing={2}>
            {/* Tags and badges */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {post.isSolved && (
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                  label="Solved"
                  size="small"
                  color="success"
                />
              )}
              {topic && (
                <Chip
                  icon={<TopicIcon sx={{ fontSize: 16 }} />}
                  label={topic.name}
                  size="small"
                  color={topic.color}
                  variant="outlined"
                />
              )}
              {post.tags.map((tag) => (
                <Chip key={tag} label={tag} size="small" />
              ))}
            </Stack>

            {/* Title */}
            <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
              {post.title}
            </Typography>

            {/* Author info */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>
                {post.author[0].toUpperCase()}
              </Avatar>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {post.author}
                  </Typography>
                  {author && (
                    <Chip label={`${author.reputation} rep`} size="small" variant="outlined" />
                  )}
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <AccessTimeIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {formatDate(post.createdAt)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <VisibilityIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {post.views} views
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </Stack>

            <Divider />

            {/* Post Content */}
            <Box sx={{ lineHeight: 1.8 }}>
              {renderContent(post.content)}
            </Box>

            <Divider />

            {/* Post Actions */}
            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  startIcon={<ThumbUpOutlinedIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {post.likes} Likes
                </Button>
                <Button
                  size="small"
                  startIcon={<BookmarkBorderIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  startIcon={<ShareIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Share
                </Button>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Card>

      {/* Demo mode notice */}
      {isDemo && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You're viewing this post in demo mode. Sign up to reply and participate in discussions!
        </Alert>
      )}

      {/* Reply Form */}
      {canPost && (
        <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
          <form onSubmit={handleSubmitReply}>
            <Stack spacing={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Your Reply
              </Typography>
              <TextField
                multiline
                rows={4}
                placeholder="Share your thoughts, experience, or solution..."
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                fullWidth
              />
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!replyContent.trim() || submitting}
                  sx={{ textTransform: 'none' }}
                >
                  {submitting ? 'Posting...' : 'Post Reply'}
                </Button>
              </Stack>
            </Stack>
          </form>
        </Card>
      )}

      {/* Replies Section */}
      <Stack spacing={2}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
          {replies.length} {replies.length === 1 ? 'Reply' : 'Replies'}
        </Typography>

        {/* Accepted Answer First */}
        {acceptedAnswer && (
          <Box>
            <Typography variant="subtitle2" sx={{ color: 'success.main', mb: 1, fontWeight: 600 }}>
              Accepted Answer
            </Typography>
            <ReplyCard reply={acceptedAnswer} isAccepted />
          </Box>
        )}

        {/* Other Replies */}
        {otherReplies.length > 0 && (
          <Stack spacing={1.5}>
            {acceptedAnswer && (
              <Typography variant="subtitle2" sx={{ color: 'text.secondary', mt: 2 }}>
                Other Replies
              </Typography>
            )}
            {otherReplies.map((reply) => (
              <ReplyCard key={reply.id} reply={reply} isAccepted={false} />
            ))}
          </Stack>
        )}

        {/* No replies */}
        {replies.length === 0 && (
          <Card variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'action.hover' }}>
            <Typography sx={{ color: 'text.secondary' }}>
              No replies yet. Be the first to respond!
            </Typography>
          </Card>
        )}
      </Stack>
    </Box>
  );
}

export default function ForumPostPage(props) {
  const location = useLocation();
  const isDemo = location.pathname.startsWith('/demo');

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <DataModeProvider mode={isDemo ? DATA_MODES.DEMO : DATA_MODES.LIVE}>
          <GrowProvider>
            <CommunityProvider>
              <SideMenu />
              <AppNavbar />
              <Box
                component="main"
                sx={(theme) => ({
                  flexGrow: 1,
                  backgroundColor: theme.vars
                    ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
                    : alpha(theme.palette.background.default, 1),
                  overflow: 'auto',
                  minHeight: '100vh',
                })}
              >
                <Stack
                  sx={{
                    pt: { xs: 10, md: 3 },
                    pb: 5,
                  }}
                >
                  <PostContent />
                </Stack>
              </Box>
            </CommunityProvider>
          </GrowProvider>
        </DataModeProvider>
      </Box>
    </AppTheme>
  );
}
